import { generateValley } from "./valley";

// Deterministic map generators. Server and clients both run this from the
// same seed, so the map never goes over the wire — only chests, signs and
// players are dynamic. The seed also picks the map type (see mapTypeOf).

const ISLAND_SIZE = 96;
const ISLAND_HEIGHT = 40;
export const WATER_LEVEL = 10;

export const B = {
  AIR: 0,
  GRASS: 1,
  DIRT: 2,
  STONE: 3,
  SAND: 4,
  WATER: 5,
  LOG: 6,
  LEAVES: 7,
  PLANKS: 8,
  COBBLE: 9,
  BEDROCK: 10,
  STONEBRICK: 11,
  GOLD: 12,
  GLASS: 13,
  // Valleyside's town blocks.
  ASPHALT: 14,
  BRICK: 15,
  BOOKSHELF: 16,
  CONCRETE: 17,
  WOOL_WHITE: 18,
  WOOL_RED: 19,
  WOOL_BLUE: 20,
  WOOL_YELLOW: 21,
  WOOL_BLACK: 22,
} as const;

export const SOLID = new Uint8Array(32);
for (const b of [
  B.GRASS, B.DIRT, B.STONE, B.SAND, B.LOG, B.LEAVES, B.PLANKS, B.COBBLE, B.BEDROCK, B.STONEBRICK, B.GOLD, B.GLASS,
  B.ASPHALT, B.BRICK, B.BOOKSHELF, B.CONCRETE, B.WOOL_WHITE, B.WOOL_RED, B.WOOL_BLUE, B.WOOL_YELLOW, B.WOOL_BLACK,
]) SOLID[b] = 1;

export type Vec3i = { x: number; y: number; z: number };

export type MapType = "island" | "valley";

/** A chest spot. `good` ones (the cornucopia, a hidden prize or two) roll the better middle loot. */
export interface MapChest extends Vec3i {
  good: boolean;
}

export interface MapData {
  type: MapType;
  world: World;
  chests: MapChest[];
  pedestals: { x: number; y: number; z: number; yaw: number }[];
  center: Vec3i;
  /** Deathmatch arena radius around the centre; outside it hurts. */
  arenaRadius: number;
  /** Named spots worth visiting (Valleyside's buildings, the waterfall, the cabin...). */
  landmarks: Record<string, Vec3i>;
}

/**
 * Seeds with bit 30 set are Valleyside, the rest are islands. New worlds roll a
 * random 31-bit seed, so that's about half and half, and a full world's extra
 * copies (seed + n) keep their type.
 */
export const VALLEY_SEED_BIT = 1 << 30;
export function mapTypeOf(seed: number): MapType {
  return seed & VALLEY_SEED_BIT ? "valley" : "island";
}

/** Packs a block position into one number for chest lookups. Good for maps up to 1024 wide. */
export function blockKey(x: number, y: number, z: number) {
  return (y * 1024 + z) * 1024 + x;
}

export class World {
  readonly blocks: Uint8Array;
  constructor(readonly sx: number, readonly sy: number, readonly sz: number) {
    this.blocks = new Uint8Array(sx * sy * sz);
  }
  get(x: number, y: number, z: number): number {
    if (x < 0 || z < 0 || x >= this.sx || z >= this.sz || y >= this.sy) return B.AIR;
    if (y < 0) return B.BEDROCK;
    return this.blocks[(y * this.sz + z) * this.sx + x]!;
  }
  set(x: number, y: number, z: number, b: number) {
    if (x < 0 || z < 0 || y < 0 || x >= this.sx || z >= this.sz || y >= this.sy) return;
    this.blocks[(y * this.sz + z) * this.sx + x] = b;
  }
  solid(x: number, y: number, z: number): boolean {
    // Map edges are invisible walls so nobody swims off into the void.
    if (x < 0 || z < 0 || x >= this.sx || z >= this.sz) return true;
    return SOLID[this.get(x, y, z)] === 1;
  }
  topSolid(x: number, z: number): number {
    for (let y = this.sy - 1; y >= 0; y--) if (this.solid(x, y, z)) return y;
    return 0;
  }
}

// Integer hash -> [0,1). No Math.sin, so results are bit-identical everywhere.
export function hash(x: number, z: number, seed: number): number {
  let h = (x * 374761393 + z * 668265263 + seed * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h = h ^ (h >>> 16);
  return (h >>> 0) / 4294967296;
}

export function valueNoise(x: number, z: number, scale: number, seed: number): number {
  const fx = x / scale;
  const fz = z / scale;
  const x0 = Math.floor(fx);
  const z0 = Math.floor(fz);
  const tx = fx - x0;
  const tz = fz - z0;
  const sx = tx * tx * (3 - 2 * tx);
  const sz = tz * tz * (3 - 2 * tz);
  const a = hash(x0, z0, seed);
  const b = hash(x0 + 1, z0, seed);
  const c = hash(x0, z0 + 1, seed);
  const d = hash(x0 + 1, z0 + 1, seed);
  return a + (b - a) * sx + (c - a) * sz + (a - b - c + d) * sx * sz;
}

export function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const PEDESTAL_COUNT = 12;
const PLAZA_Y = 14;

export function generateMap(seed: number): MapData {
  return mapTypeOf(seed) === "valley" ? generateValley(seed) : generateIsland(seed);
}

function generateIsland(seed: number): MapData {
  const SX = ISLAND_SIZE, SY = ISLAND_HEIGHT, SZ = ISLAND_SIZE;
  const w = new World(SX, SY, SZ);
  const cx = SX / 2;
  const cz = SZ / 2;
  const heights = new Int32Array(SX * SZ);

  for (let z = 0; z < SZ; z++) {
    for (let x = 0; x < SX; x++) {
      const dx = (x - cx) / (SX * 0.42);
      const dz = (z - cz) / (SZ * 0.42);
      const d = Math.sqrt(dx * dx + dz * dz) + (valueNoise(x, z, 14, seed + 7) - 0.5) * 0.35;
      const island = 1 - d;
      const n = valueNoise(x, z, 18, seed) * 0.65 + valueNoise(x, z, 7, seed + 3) * 0.35;
      let h = Math.floor(WATER_LEVEL - 4 + island * 12 + n * 7 * Math.max(0, island));
      // Flatten the cornucopia plaza and blend into it.
      const pd = Math.hypot(x - cx, z - cz);
      if (pd < 13) h = PLAZA_Y;
      else if (pd < 18) h = Math.round(PLAZA_Y + (h - PLAZA_Y) * ((pd - 13) / 5));
      h = Math.max(2, Math.min(SY - 12, h));
      heights[z * SX + x] = h;

      w.set(x, 0, z, B.BEDROCK);
      for (let y = 1; y <= h; y++) {
        let b: number = B.STONE;
        if (y > h - 3) b = B.DIRT;
        if (y === h) b = h <= WATER_LEVEL + 1 ? B.SAND : B.GRASS;
        if (h <= WATER_LEVEL + 1 && y > h - 3) b = B.SAND;
        w.set(x, y, z, b);
      }
      for (let y = h + 1; y <= WATER_LEVEL; y++) w.set(x, y, z, B.WATER);
    }
  }

  const rand = mulberry32(seed * 31 + 1);
  const chests: MapChest[] = [];

  // Cornucopia: stone-brick plaza, chest ring, gold pedestals.
  for (let z = -12; z <= 12; z++) {
    for (let x = -12; x <= 12; x++) {
      const d = Math.hypot(x, z);
      if (d > 12.5) continue;
      w.set(cx + x, PLAZA_Y, cz + z, d < 4.5 ? B.STONEBRICK : d > 11.5 ? B.COBBLE : B.GRASS);
      for (let y = PLAZA_Y + 1; y < SY; y++) w.set(cx + x, y, cz + z, B.AIR);
    }
  }
  chests.push({ x: cx, y: PLAZA_Y + 1, z: cz, good: true });
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    chests.push({ x: cx + Math.round(Math.cos(a) * 3), y: PLAZA_Y + 1, z: cz + Math.round(Math.sin(a) * 3), good: true });
  }
  const pedestals: MapData["pedestals"] = [];
  for (let i = 0; i < PEDESTAL_COUNT; i++) {
    const a = (i / PEDESTAL_COUNT) * Math.PI * 2;
    const px = cx + Math.round(Math.cos(a) * 9);
    const pz = cz + Math.round(Math.sin(a) * 9);
    w.set(px, PLAZA_Y, pz, B.GOLD);
    // Face the middle. yaw 0 looks toward -z.
    const yaw = Math.atan2(-(cx - px), -(cz - pz));
    pedestals.push({ x: px + 0.5, y: PLAZA_Y + 1, z: pz + 0.5, yaw });
  }

  const occupied = (x: number, z: number) => Math.hypot(x - cx, z - cz) < 16;

  // Little plank huts, each hiding a chest.
  let huts = 0;
  const doorways: [number, number, number][] = [];
  for (let tries = 0; tries < 200 && huts < 6; tries++) {
    const hx = 8 + Math.floor(rand() * (SX - 16));
    const hz = 8 + Math.floor(rand() * (SZ - 16));
    if (occupied(hx, hz)) continue;
    let minH = 99, maxH = 0;
    for (let z = -2; z <= 2; z++) for (let x = -2; x <= 2; x++) {
      const h = heights[(hz + z) * SX + hx + x]!;
      minH = Math.min(minH, h);
      maxH = Math.max(maxH, h);
    }
    // 99 marks ground an earlier hut already took; without this a hut can land up in the sky.
    if (minH <= WATER_LEVEL + 1 || maxH - minH > 2 || maxH === 99) continue;
    const fy = maxH;
    for (let z = -2; z <= 2; z++) for (let x = -2; x <= 2; x++) {
      for (let y = minH; y <= fy; y++) w.set(hx + x, y, hz + z, B.COBBLE);
      const edge = Math.abs(x) === 2 || Math.abs(z) === 2;
      for (let y = fy + 1; y <= fy + 3; y++) {
        const corner = Math.abs(x) === 2 && Math.abs(z) === 2;
        let b: number = B.AIR;
        if (edge) b = corner ? B.LOG : y === fy + 2 && (x === 0 || z === 0) ? B.GLASS : B.PLANKS;
        w.set(hx + x, y, hz + z, b);
      }
      w.set(hx + x, fy + 4, hz + z, B.PLANKS);
      for (let y = fy + 5; y < SY; y++) w.set(hx + x, y, hz + z, B.AIR);
    }
    // Doorway on a random side.
    const side = Math.floor(rand() * 4);
    const [dx, dz] = [[0, 2], [0, -2], [2, 0], [-2, 0]][side]!;
    w.set(hx + dx!, fy + 1, hz + dz!, B.AIR);
    w.set(hx + dx!, fy + 2, hz + dz!, B.AIR);
    chests.push({ x: hx - dx! / 2, y: fy + 1, z: hz - dz! / 2, good: false });
    // The floor can sit a couple of blocks above the ground outside: cobble steps down from the door.
    for (let k = 0; k <= 3; k++) {
      const sx = hx + dx! + (dx! / 2) * k, sz = hz + dz! + (dz! / 2) * k;
      if (k && heights[sz * SX + sx] === 99) break;
      if (k) for (let y = fy - k; y > 0 && !w.solid(sx, y, sz); y--) w.set(sx, y, sz, B.COBBLE);
      for (let y = fy - k + 1; y <= fy + 2; y++) {
        w.set(sx, y, sz, B.AIR);
        doorways.push([sx, y, sz]);
      }
      heights[sz * SX + sx] = 99;
    }
    for (let z = -3; z <= 3; z++) for (let x = -3; x <= 3; x++) heights[(hz + z) * SX + hx + x] = 99;
    huts++;
  }

  // Trees, with the occasional chest tucked at the trunk.
  for (let tries = 0; tries < 900; tries++) {
    const tx = 3 + Math.floor(rand() * (SX - 6));
    const tz = 3 + Math.floor(rand() * (SZ - 6));
    const h = heights[tz * SX + tx]!;
    if (h === 99 || occupied(tx, tz) || w.get(tx, h, tz) !== B.GRASS) continue;
    if (w.get(tx, h + 1, tz) !== B.AIR) continue;
    if (rand() > 0.35) continue;
    const trunk = 4 + Math.floor(rand() * 2);
    for (let y = 1; y <= trunk; y++) w.set(tx, h + y, tz, B.LOG);
    for (let y = trunk - 2; y <= trunk + 1; y++) {
      const r = y >= trunk ? 1 : 2;
      for (let z = -r; z <= r; z++) for (let x = -r; x <= r; x++) {
        if (Math.abs(x) === r && Math.abs(z) === r && (y === trunk + 1 || rand() < 0.5)) continue;
        if (w.get(tx + x, h + y, tz + z) === B.AIR) w.set(tx + x, h + y, tz + z, B.LEAVES);
      }
    }
    if (rand() < 0.12 && w.get(tx + 1, h + 1, tz) === B.AIR && w.get(tx + 1, h, tz) === B.GRASS) {
      chests.push({ x: tx + 1, y: h + 1, z: tz, good: false });
      // Lift the bottom of the canopy off head height so you can walk up to the chest.
      for (let z = -2; z <= 2; z++) for (let x = -2; x <= 2; x++) {
        if (w.get(tx + x, h + 2, tz + z) === B.LEAVES) w.set(tx + x, h + 2, tz + z, B.AIR);
      }
    }
    for (let z = -2; z <= 2; z++) for (let x = -2; x <= 2; x++) {
      const i = (tz + z) * SX + tx + x;
      if (i >= 0 && i < heights.length) heights[i] = 99;
    }
  }

  // Leaves from a later tree can grow into a chest's spot or across a hut door.
  for (const c of chests) if (w.solid(c.x, c.y, c.z)) w.set(c.x, c.y, c.z, B.AIR);
  for (const [x, y, z] of doorways) if (w.get(x, y, z) === B.LEAVES) w.set(x, y, z, B.AIR);
  return { type: "island", world: w, chests, pedestals, center: { x: cx, y: PLAZA_Y + 1, z: cz }, arenaRadius: 13, landmarks: {} };
}
